import { createContext, useContext, useState, useEffect, ReactNode, SetStateAction, Dispatch } from "react";
import { NavOptions } from "../../../types";
import { InsightEventGroupEnum } from "../constants";
import { useInsightTabStore } from "../insight-tab-store";

/**@ts-ignore*/
import footballField from "../../../../../../assets-new/images/field_map_with_padding.png";
/**@ts-ignore*/
import zoneFieldMap from "../../../../../../assets-new/images/field_zone_map_with_padding.png";

interface CategoryTabContextType {
	activeCategoryTab: InsightEventGroupEnum;
	setActiveCategoryTab: (tab: InsightEventGroupEnum) => void;
}

// ---------- Team Info Context ----------

export interface EventListTeamInfo {
	homeTeam: {
		id: number;
		shirtColor: string | null;
		clubLogo?: string | null;
	};
	awayTeam: {
		id: number;
		shirtColor: string | null;
		clubLogo?: string | null;
	};
}

interface TeamInfoContextType {
	teamInfo: EventListTeamInfo | null;
	setTeamInfo: Dispatch<SetStateAction<EventListTeamInfo | null>>;
}

const TeamInfoContext = createContext<TeamInfoContextType | undefined>(undefined);

export const useTeamInfo = () => {
	const context = useContext(TeamInfoContext);
	if (!context) {
		throw new Error("useTeamInfo must be used within a CombinedProvider");
	}
	return context;
};

// ---------- Nav Options Context ----------

interface NavOptionsContextType {
	handleChangeNavOptions: (option: NavOptions) => void;
}

const NavOptionsContext = createContext<NavOptionsContextType | undefined>(undefined);

export const useNavOptions = () => {
	const context = useContext(NavOptionsContext);
	if (!context) {
		throw new Error("useNavOptions must be used within a CombinedProvider");
	}
	return context;
};

// ---------- Field Image Cache Context ----------

interface FieldImageContextType {
	footballFieldImage: HTMLImageElement | null;
	zoneFieldImage: HTMLImageElement | null;
}

const FieldImageContext = createContext<FieldImageContextType | undefined>(undefined);

export const useFieldImages = () => {
	const context = useContext(FieldImageContext);
	if (!context) {
		throw new Error("useFieldImages must be used within a CombinedProvider");
	}
	return context;
};

interface DataInsightProviderProps {
	children: ReactNode;
	handleChangeNavOptions: (option: NavOptions) => void;
}

const CategoryTabContext = createContext<CategoryTabContextType | undefined>(undefined);

export const useCategoryTab = () => {
	const context = useContext(CategoryTabContext);
	if (!context) {
		throw new Error("useCategoryTab must be used within a CombinedProvider");
	}
	return context;
};

export const DataInsightProvider = ({ children, handleChangeNavOptions }: DataInsightProviderProps) => {
	const [teamInfo, setTeamInfo] = useState<EventListTeamInfo | null>(null);
	// Use Zustand store as source of truth for tab state - context is just a wrapper for backward compatibility
	const activeCategoryTab = useInsightTabStore((state) => state.activeCategoryTab);
	const setActiveCategoryTab = useInsightTabStore((state) => state.setActiveCategoryTab);
	const [footballFieldImage, setFootballFieldImage] = useState<HTMLImageElement | null>(null);
	const [zoneFieldImage, setZoneFieldImage] = useState<HTMLImageElement | null>(null);

	// Load field images once and cache them for all widgets
	useEffect(() => {
		const loadFootballField = new window.Image();
		loadFootballField.crossOrigin = "anonymous";
		loadFootballField.onload = () => {
			setFootballFieldImage(loadFootballField);
		};
		loadFootballField.src = footballField;

		const loadZoneField = new window.Image();
		loadZoneField.crossOrigin = "anonymous";
		loadZoneField.onload = () => {
			setZoneFieldImage(loadZoneField);
		};
		loadZoneField.src = zoneFieldMap;
	}, []);

	return (
		<NavOptionsContext.Provider value={{ handleChangeNavOptions }}>
			<TeamInfoContext.Provider value={{ teamInfo, setTeamInfo }}>
				<CategoryTabContext.Provider value={{ activeCategoryTab, setActiveCategoryTab }}>
					<FieldImageContext.Provider value={{ footballFieldImage, zoneFieldImage }}>{children}</FieldImageContext.Provider>
				</CategoryTabContext.Provider>
			</TeamInfoContext.Provider>
		</NavOptionsContext.Provider>
	);
};
